import React, { useState, useContext, useEffect } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Container, 
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import { useAppNavigation } from '../hooks/useAppNavigation';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LogoutIcon from '@mui/icons-material/Logout';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { AuthContext } from '../context/AuthContext';
import Avatar from '../components/Avatar';
import EncryptionSettings from '../components/settings/EncryptionSettings';
import { getBackendBaseUrl } from '../utils/configUtils';

const Profile = () => {
  const navigate = useAppNavigation();
  const { currentUser, logout, updateProfile, error: authError } = useContext(AuthContext);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  
  // Helper function to get user display name from various possible fields
  const getUserDisplayName = (user) => {
    if (!user) return 'User';
    
    // Try different possible name fields
    return user.name || user.full_name || user.email?.split('@')[0] || 'User';
  };

  // Initialize form with current user data
  useEffect(() => {
    if (currentUser) {
      // Use any available name field
      const displayName = getUserDisplayName(currentUser);
      setName(displayName);
      setEmail(currentUser.email || '');
      
      console.log('Profile initialized with user data:', {
        displayName,
        email: currentUser.email,
        user: currentUser
      });
    }
  }, [currentUser]);
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Update profile
      const success = await updateProfile({ name });
      
      if (success) {
        setSuccess(true);
        // Reset success message after delay
        setTimeout(() => {
          setSuccess(false);
        }, 3000);
      } else {
        setError('Failed to update profile. Please try again.');
      }
      
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to update profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle logout
  const handleLogout = () => {
    logout();
  };
  
  // Handle copy JWT token to clipboard
  const handleCopyToken = () => {
    const token = localStorage.getItem('cubxly_token');
    if (token) {
      navigator.clipboard.writeText(token).then(() => {
        setCopySuccess(true);
      }).catch(err => {
        console.error('Failed to copy token:', err);
        setError('Failed to copy token to clipboard');
      });
    }
  };
  
  // Get admin panel URL with JWT token
  const getAdminPanelUrl = () => {
    const backendUrl = getBackendBaseUrl();
    const token = localStorage.getItem('cubxly_token') || '';
    return `${backendUrl}/api/admin/?token=${encodeURIComponent(token)}`;
  };
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="back"
            onClick={() => navigate.to.HOME()}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            Profile
          </Typography>
        </Toolbar>
      </AppBar>
      
      {/* Main Content */}
      <Container component="main" sx={{ flexGrow: 1, py: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {authError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {authError}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Profile updated successfully!
          </Alert>
        )}
        
        <Paper sx={{ p: 3 }}>
          {/* Avatar and User Information */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            {currentUser ? (
              <Avatar 
                user={{
                  email: currentUser.email,
                  name: getUserDisplayName(currentUser),
                  profile_picture: currentUser.profile_picture
                }}
                size={80}
                sx={{ mb: 2 }}
              />
            ) : (
              <Avatar 
                user={{
                  email: "",
                  name: "Loading..."
                }}
                size={80}
                sx={{ mb: 2 }}
              />
            )}
            
            <Typography variant="h6" gutterBottom>
              {getUserDisplayName(currentUser)}
            </Typography>
            
            <Typography variant="body2" color="text.secondary">
              {currentUser?.email || 'No email provided'}
            </Typography>
          </Box>
          
          {/* Profile Form */}
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              margin="normal"
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isLoading}
              required
            />
            
            <TextField
              fullWidth
              margin="normal"
              label="Email"
              value={email}
              disabled={true} // Email cannot be changed
              helperText="Email cannot be changed"
            />
            
            {/* Submit Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={isLoading}
              sx={{ mt: 3 }}
            >
              {isLoading ? <CircularProgress size={24} /> : 'Update Profile'}
            </Button>
            
            {/* Logout Button */}
            <Button
              fullWidth
              variant="outlined"
              color="error"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              disabled={isLoading}
              sx={{ mt: 2 }}
            >
              Logout
            </Button>
          </Box>

          {/* Encryption Settings Section */}
          <Box sx={{ mt: 4, p: 3, border: '1px solid #e0e0e0', borderRadius: '4px' }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2 }}>
              Encryption Settings
            </Typography>
            <EncryptionSettings />
          </Box>

          {/* Admin Tools Section - Only visible to admin users */}
          {currentUser?.is_admin && (
            <Box sx={{ 
              mt: 4, 
              p: 3, 
              border: '2px solid #ff6b35', 
              borderRadius: '4px',
              background: 'linear-gradient(135deg, #fff5f3 0%, #ffe8e3 100%)'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AdminPanelSettingsIcon sx={{ mr: 1, color: '#ff6b35', fontSize: 28 }} />
                <Typography variant="h5" component="h2" sx={{ color: '#ff6b35', fontWeight: 600 }}>
                  Admin Tools
                </Typography>
              </Box>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                You are logged in as an administrator. Click the button below to access the admin panel with automatic authentication.
              </Alert>
              
              <Button
                fullWidth
                variant="contained"
                href={getAdminPanelUrl()}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  bgcolor: '#ff6b35',
                  '&:hover': { bgcolor: '#e85a25' },
                  mb: 2,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600
                }}
              >
                🚀 Open Admin Panel
              </Button>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Or copy your admin JWT token manually:
              </Typography>
              
              <Box sx={{ 
                p: 2, 
                bgcolor: '#f5f5f5', 
                borderRadius: '4px', 
                mb: 2,
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                wordBreak: 'break-all',
                border: '1px solid #ddd',
                maxHeight: '80px',
                overflow: 'auto'
              }}>
                {localStorage.getItem('cubxly_token') || 'Token not found'}
              </Box>
              
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyToken}
                sx={{ 
                  borderColor: '#ff6b35',
                  color: '#ff6b35',
                  '&:hover': { 
                    borderColor: '#e85a25',
                    bgcolor: 'rgba(255, 107, 53, 0.04)'
                  }
                }}
              >
                Copy Token to Clipboard
              </Button>
              
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                💡 Access analytics dashboard, user management, promotion tools, and more from the admin panel.
              </Typography>
            </Box>
          )}

        </Paper>
      </Container>
      
      {/* Copy Success Snackbar */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(false)}
        message="✅ Admin token copied to clipboard!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default Profile;
